// Mirrors the local devis record to the platform: every change to `nb-devis` is pushed to
// devis/update.php, through the same-origin proxy (the strict CSP forbids talking to the
// platform directly, §10).
//
// Two guards make this safe to hang off every write:
//   - DEBOUNCED, because the record is rewritten on each keystroke of the lead form. Sending
//     one request per character would hammer the API for a value the visitor is still typing.
//   - IDENTIFIED ONLY: without an id there is nothing to update upstream — the devis does not
//     exist yet. Those writes are simply kept local until it does.
//
// Fire-and-forget: a failed sync is logged, never surfaced. The record stays in localStorage
// and the next change re-sends the whole thing, so nothing is lost by one lost request.

import { readDevis, onDevisChanged } from './devisRecord.js';

const URL_UPDATE = '/api/devis/update';
const DEBOUNCE_MS = 1000;

let timer = null;
let lastSent = '';

/** Wire the record to the platform. Called once at bootstrap, for every page. */
export function initDevisSync() {
  onDevisChanged(() => schedule());
}

function schedule() {
  clearTimeout(timer);
  timer = setTimeout(push, DEBOUNCE_MS);
}

async function push() {
  const devis = readDevis();
  if (!devis.id) return;

  const payload = JSON.stringify(devis);
  // The debounce already collapses a burst; this drops the rest — a save that changed nothing
  // (a re-render, a field set back to its previous value) is not worth a round-trip.
  if (payload === lastSent) return;
  lastSent = payload;

  try {
    const res = await fetch(URL_UPDATE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Never flagged. `paa` starts the platform's follow-up and belongs to one moment — the
      // quote being produced — which the SERVER announces, on the machine that made it
      // (server/services/devis-service.js). A mirror of the record is not that moment.
      body: JSON.stringify({ devis: payload }),
    });
    if (!res.ok) {
      // Let the next change retry from scratch rather than assume this state reached them.
      lastSent = '';
      console.error(`[devisSync] update responded ${res.status}`);
    }
  } catch (error) {
    lastSent = '';
    console.error('[devisSync] update unreachable', error);
  }
}
